import { Button } from "@/components/ui/button.tsx"
import { ClickToCopyRemotePromoId } from "@/components/click_to_copy_remote_promo_id.tsx"
import { OpenPromotionLink } from "@/components/open_promotion_link.tsx"
import { Input } from "@/components/ui/input.tsx"
import { Label } from "@/components/ui/label.tsx"
import { Checkbox } from "@/components/ui/checkbox.tsx"
import { Copy, Edit2, Trash2, Check, Star, Eye } from "lucide-react"
import { ItemBadge } from "../item_badge.tsx"
import { useState, useEffect, useRef } from "preact/hooks"
import React from "preact/compat"
import { Split } from "@/api/accounting.tsx"
import { PromotionMatch, PromotionSearchContextSeed } from "@/accounting_detail/PromotionSelector.tsx"
import { format, parseISO } from "date-fns"
import { exactCurrencyFormatter } from "@/utils"
import { OrgFieldConfig } from "@/api/org_field_config.tsx"
import { SplitPreferencesState, setAdditionalFieldsOpenByDefault } from "./split_preferences.tsx"
import { SplitFormFields } from "./split_form_fields.tsx"
import { api_fetch } from "@/api/client.tsx"
import { evaluateAmountInput } from "../amount_expression.ts"

interface SplitCardProps {
  split: Split
  index: number
  isEditing: boolean
  onUpdate: (field: keyof Split, value: any) => void
  onDelete: () => void
  onDuplicate: (splitId: string) => void
  onStartEdit: () => void
  onStopEdit: () => void
  promotionMatches?: PromotionMatch[]
  hasUnsavedChanges?: boolean
  orgFieldConfigs?: OrgFieldConfig[]
  orgId?: string
  promotionSearchContextSeed?: PromotionSearchContextSeed
}

export function SplitCard({
  split,
  index,
  isEditing,
  onUpdate,
  onDelete,
  onDuplicate,
  onStartEdit,
  onStopEdit,
  promotionMatches = [],
  hasUnsavedChanges = false,
  orgFieldConfigs = [],
  orgId,
  promotionSearchContextSeed,
}: SplitCardProps) {
  const [localAmount, setLocalAmount] = useState(formatAmount(split.amount))
  const [promotionDetails, setPromotionDetails] = useState<{name: string, type?: string} | null>(null)
  const editRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setLocalAmount(formatAmount(split.amount))
  }, [split.amount])

  // Look up promotion details from parent-provided matches
  // This avoids individual API calls for each split component
  useEffect(() => {
    if (split.promotion_id && !split.promotion_name && promotionMatches.length > 0) {
      const match = promotionMatches.find(m => m.promotion.id === split.promotion_id)
      if (match) {
        setPromotionDetails({
          name: match.promotion.name,
          type: match.promotion.promotion_type
        })
      }
    }
  }, [split.promotion_id, split.promotion_name, promotionMatches])

  // No automatic click-outside handling - edit mode only closes via explicit user actions
  // This prevents interference with dropdown selections and form interactions

  function handleAmountChange(e: Event) {
    const target = e.target as HTMLInputElement
    const newValue = target.value

    if (newValue === "") {
      setLocalAmount("")
      return
    }

    const isValidInput = /^[\d+\-*/().,\s$]*$/.test(newValue)
    if (!isValidInput) {
      return
    }

    setLocalAmount(newValue)
  }

  function handleAmountBlur(e: Event) {
    const target = e.target as HTMLInputElement
    const parsedValue = evaluateAmountInput(target.value)
    if (parsedValue === null) {
      setLocalAmount(formatAmount(split.amount))
      return
    }
    const formattedValue = formatAmount(parsedValue)

    setLocalAmount(formattedValue)
    onUpdate("amount", parsedValue)
  }

  if (!isEditing) {
    return <SplitCardView 
      split={split} 
      index={index} 
      onStartEdit={onStartEdit} 
      onDelete={onDelete}
      promotionMatches={promotionMatches}
      orgFieldConfigs={orgFieldConfigs}
      orgId={orgId}
      promotionSearchContextSeed={promotionSearchContextSeed}
    />
  }

  return <SplitCardEdit 
    ref={editRef}
    split={split} 
    index={index} 
    localAmount={localAmount}
    onAmountChange={handleAmountChange}
    onAmountBlur={handleAmountBlur}
    onUpdate={onUpdate}
    onDelete={onDelete}
    onDuplicate={onDuplicate}
    hasUnsavedChanges={hasUnsavedChanges}
    orgFieldConfigs={orgFieldConfigs}
    orgId={orgId}
    promotionSearchContextSeed={promotionSearchContextSeed}
  />
}

interface SplitCardViewProps {
  split: Split
  index: number
  onStartEdit: () => void
  onDelete: () => void
  promotionMatches?: PromotionMatch[]
  orgFieldConfigs?: OrgFieldConfig[]
  orgId?: string
  promotionSearchContextSeed?: PromotionSearchContextSeed
}

function SplitCardView({
  split,
  index,
  onStartEdit,
  onDelete,
  promotionMatches = [],
  orgFieldConfigs = [],
  orgId,
  promotionSearchContextSeed,
}: SplitCardViewProps) {
  const [promotionDetails, setPromotionDetails] = useState<{name: string, remote_promo_id?: string} | null>(null)
  const [showReadOnlyForm, setShowReadOnlyForm] = useState(false)

  // Fetch promotion details directly from API if not available in promotionMatches
  // This ensures we always have the remote_promo_id when it exists
  useEffect(() => {
    if (!split.promotion_id) {
      setPromotionDetails(null)
      return
    }

    // First try to find in promotionMatches prop (if provided)
    if (promotionMatches.length > 0) {
      const match = promotionMatches.find(m => m.promotion.id === split.promotion_id)
      if (match) {
        setPromotionDetails({
          name: match.promotion.name,
          remote_promo_id: match.promotion.remote_promo_id
        })
        return
      }
    }

    // If not found in promotionMatches, fetch from API (same as PromotionSelector)
    async function loadPromotionDetails() {
      try {
        const result = await api_fetch<PromotionMatch[]>("/promotion-matches", {
          method: "POST",
          body: {
            deduction_id: split.deduction_id
          },
          use_cache: true
        })

        if (result.ok) {
          const match = result.value.data.find(m => m.promotion.id === split.promotion_id)
          if (match) {
            setPromotionDetails({
              name: match.promotion.name,
              remote_promo_id: match.promotion.remote_promo_id
            })
          } else {
            setPromotionDetails(null)
          }
        }
      } catch (error) {
        console.error("Error loading promotion details:", error)
      }
    }

    loadPromotionDetails()
  }, [split.promotion_id, split.deduction_id, promotionMatches])
  
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-4 mb-2">
            <span className="text-sm font-medium text-gray-600">#{index + 1}</span>
            <span className="text-lg font-semibold">{exactCurrencyFormatter(split.amount)}</span>
            {split.account_id && (
              <span className="text-sm text-green-600 bg-green-50 px-2 py-1 rounded">
                {split.expense_account || split.name || "Account Assigned"}
              </span>
            )}
            {split.account_id && <ItemBadge item={split.product_service} />}
            {split.promotion_id && (() => {
              // Use promotionDetails as primary source (fetched from API if needed)
              // Fall back to promotionMatches prop if promotionDetails not loaded yet
              const match = promotionMatches.find(m => m.promotion.id === split.promotion_id)
              const promotionName = split.promotion_name || promotionDetails?.name || match?.promotion.name
              const remotePromoId = promotionDetails?.remote_promo_id || match?.promotion.remote_promo_id
              const displayText = promotionName || remotePromoId || "Promotion Matched"
              
              return (
                <span className="text-sm text-blue-600 bg-blue-50 px-2 py-1 rounded flex items-center gap-1">
                  <Star className="h-3 w-3" />
                  {remotePromoId && promotionName && (
                    <ClickToCopyRemotePromoId
                      copyValue={remotePromoId}
                      className="mr-1 text-xs text-blue-500 hover:underline"
                    >
                      ({remotePromoId})
                    </ClickToCopyRemotePromoId>
                  )}
                  {displayText}
                  <OpenPromotionLink promotion_id={split.promotion_id} show_label={false} className="ml-1" />
                </span>
              )
            })()}
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
            {split.retailer_name && (
              <div>
                <span className="font-medium">Retailer:</span> {split.retailer_name}
              </div>
            )}
            {split.product_line && (
              <div>
                <span className="font-medium">Product Line:</span> {split.product_line}
              </div>
            )}
            {split.code_description && (
              <div className="col-span-2">
                <span className="font-medium">Description:</span> {split.code_description}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 ml-4">
          <div
            onMouseEnter={() => setShowReadOnlyForm(true)}
            onMouseLeave={() => setShowReadOnlyForm(false)}
          >
            <Button
              variant="ghost"
              className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700"
            >
              <Eye className="h-4 w-4" />
            </Button>
          </div>
          <Button
            variant="ghost"
            onClick={onStartEdit}
            className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700"
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            onClick={onDelete}
            className="h-8 w-8 -mr-4 ml-2 p-0 text-red-500 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {showReadOnlyForm && (
        <div 
          className="mt-4 pt-4 border-t border-gray-200"
          onMouseEnter={() => setShowReadOnlyForm(true)}
          onMouseLeave={() => setShowReadOnlyForm(false)}
        >
          <SplitFormFields
            split={split}
            onUpdate={() => {}}
            orgFieldConfigs={orgFieldConfigs}
            orgId={orgId}
            deductionId={split.deduction_id}
            showOptionalFields={true}
            fieldIdPrefix={`view-${split.id}-`}
            promotionSearchContextSeed={promotionSearchContextSeed}
          />
        </div>
      )}
    </div>
  )
}

interface SplitCardEditProps {
  split: Split
  index: number
  localAmount: string
  onAmountChange: (e: Event) => void
  onAmountBlur: (e: Event) => void
  onUpdate: (field: keyof Split, value: any) => void
  onDelete: () => void
  onDuplicate: (splitId: string) => void
  hasUnsavedChanges?: boolean
  orgFieldConfigs?: OrgFieldConfig[]
  orgId?: string
  promotionSearchContextSeed?: PromotionSearchContextSeed
}

const SplitCardEdit = React.forwardRef<HTMLDivElement, SplitCardEditProps>(({ 
  split, 
  index, 
  localAmount, 
  onAmountChange, 
  onAmountBlur, 
  onUpdate, 
  onDelete,
  onDuplicate,
  hasUnsavedChanges = false,
  orgFieldConfigs = [],
  orgId,
  promotionSearchContextSeed,
}, ref) => {
  const preferences = SplitPreferencesState.use()
  const [isOptionalFieldsOpen, setIsOptionalFieldsOpen] = useState(preferences.additionalFieldsOpenByDefault)

  // Determine if this split should have green background
  const isNewSplit = split.id.startsWith("manual")
  const shouldShowGreen = isNewSplit || hasUnsavedChanges

  return (
    <div ref={ref} data-split-edit-card className={`${shouldShowGreen ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'} border rounded-lg p-2`}>
      <SplitFormFields
        split={split}
        onUpdate={onUpdate}
        orgFieldConfigs={orgFieldConfigs}
        orgId={orgId}
        deductionId={split.deduction_id}
        localAmount={localAmount}
        onAmountChange={onAmountChange}
        onAmountBlur={onAmountBlur}
        showOptionalFields={isOptionalFieldsOpen}
        fieldIdPrefix=""
        promotionSearchContextSeed={promotionSearchContextSeed}
        betweenRequiredAndOptional={
          <div className="flex items-center justify-between w-full mb-0">
            <Button
              type="button"
              variant="ghost"
              tabIndex={-1}
              onClick={() => setIsOptionalFieldsOpen(!isOptionalFieldsOpen)}
              className="flex-1 justify-start p-1 h-auto text-left text-sm font-medium text-gray-600 hover:text-gray-800 gap-2"
            >
              <span className={`transform transition-transform ${isOptionalFieldsOpen ? 'rotate-180' : ''}`}>
                ▼
              </span>
              <span>Additional Fields</span>
            </Button>
            <div className="flex items-center gap-2 px-2" onClick={(e: Event) => e.stopPropagation()}>
              <Checkbox
                id={`keep-open-${split.id}`}
                tabIndex={-1}
                checked={preferences.additionalFieldsOpenByDefault}
                onCheckedChange={(checked: any) => {
                  setAdditionalFieldsOpenByDefault(!!checked)
                }}
              />
              <Label
                htmlFor={`keep-open-${split.id}`}
                className="text-xs text-gray-600 cursor-pointer whitespace-nowrap"
              >
                Keep open by default
              </Label>
            </div>
          </div>
        }
      />

      <div className="flex justify-end gap-2 mt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => onDuplicate(split.id)}
          className="h-8 px-3 text-sm text-gray-600 hover:text-gray-800 gap-1"
        >
          <Copy className="h-4 w-4" />
          Duplicate Split
        </Button>
        <Button
          variant="ghost"
          onClick={onDelete}
          className="h-8 px-3 text-sm text-red-500 hover:text-red-700"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
})

// Helper function to safely parse and format numbers
function formatAmount(value: number | string): string {
  if (value === "" || value === null || value === undefined) {
    return "0.00"
  }

  const num = typeof value === "string" ? parseFloat(value) : value
  if (isNaN(num)) return "0.00"

  return num.toFixed(2)
}

// Helper function to format dates consistently
function formatDate(dateString: string): string {
  try {
    const date = parseISO(dateString)
    return format(date, 'MMM d, yyyy')
  } catch (error) {
    console.warn('Error formatting date:', dateString, error)
    return dateString
  }
}
